import type { Aircraft } from '@/types';

// ---------------------------------------------------------------------------
// Military Aircraft Tracking — adsbexchange.com
//
// adsbexchange does NOT sanitize military aircraft from its feed
// (unlike FlightAware / FlightRadar24).
//
// In dev: calls /api/military-aircraft (Vite middleware with adsbexchange key).
// Falls back to enhancing the existing adsb.lol aircraft data with better
// military detection using ICAO hex code ranges.
//
// Env var: VITE_ADSBEXCHANGE_KEY (optional — from adsbexchange.com)
// ---------------------------------------------------------------------------

// US Military ICAO hex ranges (AE0000–AEFFFF)
// UK Military: 43xxxx range
// French Military: 3C–3F range (partial)
// German Luftwaffe: 3Cxxxx
// Australian Defence: 7Cxxxx (some)
const MIL_HEX_PREFIXES = ['ae', '43'];

// Military callsign prefixes (known patterns)
const MIL_CALLSIGN_PATTERNS = [
  /^(REACH|RCH|USAF|NAVY|ARMY|USMC|USCG)/i,
  /^(CNV|ROCKY|HAWK|EAGLE|FALCON|VIPER|RAPTOR|TITAN)/i,
  /^(DUKE|JAKE|TOPAZ|ATLAS|BISON|BOXER|GLOBE|TREK)/i,
  /^(RFR|RFF|RAF|AAR|GAF|GERM|FAF|SFRA)/i,
  /^(KAF|ROKAF|JASDF|JGSDF|JMSDF)/i,
  /^(PANDA|HERKY|IRON|STEEL|THUNDER|STORM)/i,
  /^(SPAR|VENUS|MARS|JSTARS|RIVET)/i, // senior staff & ISR
];

export function isMilitaryAircraft(hex: string, callsign: string, dbFlags?: number): boolean {
  if (dbFlags && (dbFlags & 1)) return true; // adsb.lol military flag
  const hexLower = hex.toLowerCase();
  if (MIL_HEX_PREFIXES.some((p) => hexLower.startsWith(p))) return true;
  if (callsign && MIL_CALLSIGN_PATTERNS.some((r) => r.test(callsign))) return true;
  return false;
}

// ---------------------------------------------------------------------------
// Vite middleware for adsbexchange (handled in vite.config.ts via plugin)
// Client-side: fetch from /api/military-aircraft
// ---------------------------------------------------------------------------

let _cache: Aircraft[] | null = null;
let _cacheTs = 0;
const CACHE_TTL = 90_000; // 90s — adsbexchange updates every 1s but we throttle

export async function fetchMilitaryAircraft(): Promise<Aircraft[]> {
  const now = Date.now();
  if (_cache && now - _cacheTs < CACHE_TTL) return _cache;

  try {
    const res = await fetch('/api/military-aircraft', {
      signal: AbortSignal.timeout(30_000),
    });
    if (res.ok) {
      const data = await res.json() as { aircraft: Aircraft[]; count: number };
      if (data.aircraft?.length > 0) {
        _cache = data.aircraft;
        _cacheTs = now;
        console.log(`[MilAircraft] ${data.count} military aircraft from adsbexchange`);
        return _cache;
      }
    }
  } catch (err) {
    console.warn('[MilAircraft] adsbexchange fetch failed:', err);
  }

  _cache = [];
  _cacheTs = now;
  return [];
}
